import { input, semanticColor, space, typeStyle } from '@beautyburst/tokens';
import { cloneElement, useCallback, useState } from 'react';
import type { InputHTMLAttributes, ReactElement } from 'react';

export type FormErrors = Readonly<Record<string, string>>;

export interface FormErrorState {
  readonly errors: FormErrors;
  readonly setErrors: (next: FormErrors) => void;
  readonly clear: (fieldId?: string) => void;
}

/** Small reusable error state helper for presentational customer forms. */
export function useFormErrors(initial: FormErrors = {}): FormErrorState {
  const [errors, setErrors] = useState<FormErrors>(initial);
  const clear = useCallback((fieldId?: string) => {
    setErrors((current) => {
      if (fieldId === undefined) return {};
      if (current[fieldId] === undefined) return current;
      const remaining = { ...current };
      delete remaining[fieldId];
      return remaining;
    });
  }, []);
  return { errors, setErrors, clear };
}

export function validateEmail(email: string): string | undefined {
  const normalized = email.trim();
  if (normalized.length === 0) return 'Enter your email address.';
  if (normalized.length > 254) return 'Enter a valid email address.';
  const at = normalized.lastIndexOf('@');
  if (at <= 0 || at === normalized.length - 1) return 'Enter a valid email address.';
  const local = normalized.slice(0, at);
  const domain = normalized.slice(at + 1);
  if (
    local.length > 64 ||
    /[\s@]/u.test(local) ||
    /[\s@]/u.test(domain) ||
    !domain.includes('.') ||
    domain.startsWith('.') ||
    domain.endsWith('.') ||
    domain.split('.').some((part) => part.length === 0)
  ) {
    return 'Enter a valid email address.';
  }
  return undefined;
}

/**
 * Browser-safe preflight mirror of the server policy. The authoritative policy
 * remains enforced by the registration route; importing the Node auth module
 * here would put scrypt and Node built-ins in the customer bundle.
 */
export function validatePassword(password: string): string | undefined {
  if (password.length < 10) return 'Use at least 10 characters.';
  if (password.length > 200) return 'Use at most 200 characters.';
  if (!/[a-zA-Z]/u.test(password)) return 'Include a letter.';
  if (!/[0-9]/u.test(password)) return 'Include a number.';
  if (
    new Set([
      'password',
      'password1',
      'password123',
      '12345678',
      'qwerty123',
      'beautyburst',
      'letmein1',
      'iloveyou1',
    ]).has(password.toLowerCase())
  )
    return 'This password is too common.';
  return undefined;
}

export function validatePostalCode(postalCode: string): string | undefined {
  const value = postalCode.trim();
  if (value.length === 0) return undefined;
  if (!/^\d{5}(?:-\d{4})?$/u.test(value)) return 'Enter a valid ZIP code.';
  return undefined;
}

export function validatePhone(phone: string): string | undefined {
  const value = phone.trim();
  if (value.length === 0) return undefined;
  const digits = value.replace(/\D/gu, '');
  if (digits.length < 7 || digits.length > 15 || !/^[+\d().\-\s]+$/u.test(value)) {
    return 'Enter a valid phone number.';
  }
  return undefined;
}

export interface FormFieldProps {
  id: string;
  label: string;
  hint?: string;
  error?: string;
  required?: boolean;
  children: ReactElement<InputHTMLAttributes<HTMLInputElement>>;
}

/**
 * Associates an input, visible label, optional hint, and error text by IDs.
 * The child must be an input so all described-by and invalid state is kept on
 * the control rather than a decorative wrapper.
 */
export function FormField({ id, label, hint, error, required = false, children }: FormFieldProps) {
  const hintId = hint === undefined ? undefined : `${id}-hint`;
  const errorId = error === undefined ? undefined : `${id}-error`;
  const existingDescription = children.props['aria-describedby'];
  const describedBy = [existingDescription, hintId, errorId].filter(Boolean).join(' ') || undefined;
  const control = cloneElement(children, {
    id,
    required,
    'aria-invalid': error === undefined ? undefined : 'true',
    'aria-describedby': describedBy,
    style: {
      width: '100%',
      minHeight: input.height,
      padding: `0 ${input.paddingX}`,
      border: `1px solid ${error === undefined ? input.border : input.borderError}`,
      borderRadius: input.radius,
      background: input.background,
      color: input.text,
      ...children.props.style,
    },
  });

  return (
    <div style={{ display: 'grid', gap: space[2] }}>
      <label htmlFor={id} style={{ ...typeStyle('label'), color: semanticColor.textPrimary }}>
        {label}
      </label>
      {hint === undefined ? null : (
        <p
          id={hintId}
          style={{ ...typeStyle('bodySm'), color: semanticColor.textSecondary, margin: 0 }}
        >
          {hint}
        </p>
      )}
      {control}
      {error === undefined ? null : (
        <p
          id={errorId}
          style={{ ...typeStyle('bodySm'), color: semanticColor.statusDangerText, margin: 0 }}
        >
          {error}
        </p>
      )}
    </div>
  );
}

export function errorsForSummary(
  errors: FormErrors,
): readonly { fieldId: string; message: string }[] {
  return Object.entries(errors).map(([fieldId, message]) => ({ fieldId, message }));
}
